import { useCallback, useMemo } from 'react';

import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import { openRecordByIdInModal } from '@app/components/RecordModal/model/recordModalSlice';
import { CLIENT_CARD_PATH, LEAD_CARD_PATH, USERS_PATH } from '@app/constants';
import { useTaskListContext } from '@app/providers/TaskListProvider';
import { useAppDispatch } from '@app/store';

import { useLeadCardContext } from '../../../../../context';

interface UseLeadTechnicalMessageParams {
  item: LeadCardHistoryItemData;
}

interface TechnicalActionTextParts {
  prefix: string;
  suffix: string;
  objectLabel: string;
  objectLabelPrefix: string;
}

const LEAD_OBJECT_TYPE = 'Lead';
const USER_OBJECT_TYPE = 'User';
const CLIENT_OBJECT_TYPE = 'Client';
const CRM_TASK_OBJECT_TYPE = 'CrmTask';
const RECORD_OBJECT_TYPE = 'Record';
const CLIENT_COMMENT_OBJECT_TYPE = 'ClientComment';
const PAID_RECORD_ACTION_ID = 'ChangeClientRecordPaid';

const getTechnicalObjectLink = (
  objectType: string | null,
  objectId: number
): string | null => {
  switch (objectType) {
    case CLIENT_OBJECT_TYPE:
      return `${CLIENT_CARD_PATH}?id=${objectId}`;
    case LEAD_OBJECT_TYPE:
      return `${LEAD_CARD_PATH}?leadId=${objectId}`;
    case USER_OBJECT_TYPE:
      return `${USERS_PATH}/edit/?id=${objectId}&key=1`;
    default:
      return null;
  }
};

const getActionTextParts = ({
  ActionText,
  ObjectS,
  ObjectType,
}: Pick<
  LeadCardHistoryItemData,
  'ActionText' | 'ObjectS' | 'ObjectType'
>): TechnicalActionTextParts | null => {
  if (!ActionText || !ObjectS || !ObjectType) return null;

  const objectIndex = ActionText.indexOf(ObjectS);
  if (objectIndex === -1) return null;

  return {
    prefix: ActionText.slice(0, objectIndex),
    suffix: ActionText.slice(objectIndex + ObjectS.length),
    objectLabel: ObjectS,
    objectLabelPrefix: ObjectType === RECORD_OBJECT_TYPE ? `${ObjectS} ` : '',
  };
};

export const useLeadTechnicalMessage = ({
  item,
}: UseLeadTechnicalMessageParams) => {
  const dispatch = useAppDispatch();
  const { openPreviewTaskModal } = useTaskListContext();
  const { openCommentModal } = useLeadCardContext();

  const objectLink = useMemo(
    () => getTechnicalObjectLink(item.ObjectType, item.ObjectId),
    [item.ObjectId, item.ObjectType]
  );

  const actionTextParts = useMemo(
    () =>
      getActionTextParts({
        ActionText: item.ActionText,
        ObjectS: item.ObjectS,
        ObjectType: item.ObjectType,
      }),
    [item.ActionText, item.ObjectS, item.ObjectType]
  );

  const openRecordHandler = useCallback(() => {
    dispatch(openRecordByIdInModal(item.ObjectId));
  }, [dispatch, item.ObjectId]);

  const openTaskHandler = useCallback(() => {
    openPreviewTaskModal(item.ObjectId);
  }, [item.ObjectId, openPreviewTaskModal]);

  const openCommentHandler = useCallback(() => {
    openCommentModal({
      commentId: item.ObjectId,
      actionItems: item.ActionItems,
    });
  }, [item.ActionItems, item.ObjectId, openCommentModal]);

  const openObjectHandler = useMemo(() => {
    switch (item.ObjectType) {
      case CRM_TASK_OBJECT_TYPE:
        return openTaskHandler;
      case RECORD_OBJECT_TYPE:
        return openRecordHandler;
      case CLIENT_COMMENT_OBJECT_TYPE:
        return openCommentHandler;
      default:
        return null;
    }
  }, [item.ObjectType, openCommentHandler, openRecordHandler, openTaskHandler]);

  return {
    actionTextParts,
    isCommentAction: item.ObjectType === CLIENT_COMMENT_OBJECT_TYPE,
    objectLink,
    openObjectHandler,
    openPaidRecordHandler: openRecordHandler,
    shouldShowPaidRecordLink: item.ActionId === PAID_RECORD_ACTION_ID,
  };
};
